import { Field, Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import ActionSpinner from 'src/components/loaders/action-spinner';
import useActionPanelStore from 'src/stores/useActionPanelStore';
import { apiWithContext } from 'src/utils/api';
import {
  API_RESOURCES,
  TOOLING_STOCK_ACTION_TYPES,
} from 'src/utils/constants';

const ToolActionForm = ({
  tool,
  action,
  closePanel,
  handleSubmit,
  setError,
  customFieldReferences,
}) => {
  const [locationOptions, setLocationOptions] = useState([]);
  const [subLocationOptions, setSubLocationOptions] = useState([]);
  const [loadingSubLocations, setLoadingSubLocations] = useState(false);

  const [loading, setLoading] = useState(false);

  const { setActionPanelProps } = useActionPanelStore();

  const locationSelectRequired = (action) => ([TOOLING_STOCK_ACTION_TYPES.RELOCATE, TOOLING_STOCK_ACTION_TYPES.MAKE_READY_FOR_USE].includes(action));
  const notesRequired = (action) => ([TOOLING_STOCK_ACTION_TYPES.RECORD_TESTS, TOOLING_STOCK_ACTION_TYPES.COMPLETE_REFURBISH].includes(action));
  const hasCustomFieldReferences = customFieldReferences.length > 0;

  const onLocationChange = async (location) => {
    setLoadingSubLocations(true);
    if (!location) {
      setSubLocationOptions([]);
    }
    const api = apiWithContext();
    const { resources: subLocations } = await api.get(`${API_RESOURCES.SUB_LOCATION}/`, {
      searchParams: {
        'filter[location]': location,
      },
    }).json();
    setSubLocationOptions(subLocations);
    setLoadingSubLocations(false);
  };

  const fetchLocations = async () => {
    if (locationSelectRequired(action)) {
      setLoading(true);
      const api = apiWithContext();
      try {
        const { resources: locations } = await api.get(`${API_RESOURCES.LOCATION}/`, {}).json();
        setLocationOptions(locations);
      } catch (error) {
        console.error(error);
        setActionPanelProps({ errorMessage: error.message });
        if (setError) {
          setError(error.message);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchLocations();
  }, []);

  if (loading || !tool) {
    return (
      <ActionSpinner />
    );
  }

  return (
    <Formik
      initialValues={{
        location: '',
        subLocation: '',
        associatedCustomFields: {},
        customFieldValues: {},
      }}
      onSubmit={(form) => handleSubmit(form, action)}
    >
      {({ isSubmitting, setFieldValue, handleChange, values }) => {
        const renderCustomFieldInput = (customFieldReference) => {
          const fieldUri = customFieldReference.uri;
          const fieldType = customFieldReference?.type;
          const fieldOptions = customFieldReference?.options || [];
          const isAssociated = !!values.associatedCustomFields[fieldUri];
          const currentValue = values.customFieldValues[fieldUri];

          if (fieldOptions.length) {
            return (
              <select
                className="form-control"
                id={`custom-field-value-${fieldUri}`}
                value={currentValue || ''}
                disabled={!isAssociated}
                onChange={(event) => {
                  setFieldValue('customFieldValues', {
                    ...values.customFieldValues,
                    [fieldUri]: event.target.value,
                  });
                }}
              >
                <option value="">Choose...</option>
                {fieldOptions.map((option) => (
                  <option key={`${fieldUri}-${option.value}`} value={option.value}>{option.key}</option>
                ))}
              </select>
            );
          }

          if (fieldType === 'bool' || fieldType === 'boolean') {
            return (
              <div className="form-check">
                <input
                  id={`custom-field-value-${fieldUri}`}
                  type="checkbox"
                  className="form-check-input"
                  checked={Boolean(currentValue)}
                  disabled={!isAssociated}
                  onChange={(event) => {
                    setFieldValue('customFieldValues', {
                      ...values.customFieldValues,
                      [fieldUri]: event.target.checked,
                    });
                  }}
                />
                <label className="form-check-label" htmlFor={`custom-field-value-${fieldUri}`}>True / False</label>
              </div>
            );
          }

          const inputType = fieldType === 'date'
            ? 'date'
            : ['float', 'integer', 'int', 'number'].includes(fieldType)
              ? 'number'
              : 'text';

          return (
            <input
              type={inputType}
              className="form-control"
              id={`custom-field-value-${fieldUri}`}
              disabled={!isAssociated}
              step={inputType === 'number' ? 'any' : undefined}
              value={currentValue || ''}
              onChange={(event) => {
                setFieldValue('customFieldValues', {
                  ...values.customFieldValues,
                  [fieldUri]: event.target.value,
                });
              }}
            />
          );
        };

        return (
          <Form>
            {locationSelectRequired(action) && (
              <>
                <label className="text-left d-block" htmlFor="location">Location: *</label>
                <Field
                  required
                  component="select"
                  id="location"
                  name="location"
                  className="form-control mb-4"
                  onChange={(e) => {
                    handleChange(e);
                    void onLocationChange(e.target.value);
                    setFieldValue('subLocation', '');
                  }}
                >
                  <option value="">Choose...</option>
                  {locationOptions.map((location) => (
                    <option key={location?.uri} value={location?.uri}>{location?.name}</option>
                  ))}
                </Field>
                <label className="text-left d-block" htmlFor="subLocation">Sub-Location:</label>
                <Field
                  component="select"
                  className="form-control mb-4"
                  id="subLocation"
                  name="subLocation"
                  disabled={loadingSubLocations}
                >
                  <option value="">Choose...</option>
                  {subLocationOptions.map((subLocation) => (
                    <option key={subLocation?.uri} value={subLocation?.uri}>{subLocation?.name}</option>
                  ))}
                </Field>
              </>
            )}

            <label className="text-left d-block" htmlFor="notes">Notes:{notesRequired(action) && ' *'}</label>
            <Field
              required={notesRequired(action)}
              className="form-control mb-4"
              id="notes"
              name="notes"
              as="textarea"
            />

            <div className="text-left mb-4">
              <h5 className="mb-3">Tooling Stock Custom Fields</h5>
              {!hasCustomFieldReferences && (
                <div className="text-muted">No custom fields available for tooling stock.</div>
              )}
              {hasCustomFieldReferences && customFieldReferences.map((customFieldReference) => {
                const fieldUri = customFieldReference.uri;
                const fieldLabel = customFieldReference.field_name || customFieldReference.field_id || fieldUri;
                const isAssociated = !!values.associatedCustomFields[fieldUri];

                return (
                  <div key={fieldUri} className="mb-3">
                    <button
                      type="button"
                      className={`tooling-stock-custom-field-row ${isAssociated ? 'tooling-stock-custom-field-row--selected' : ''}`}
                      aria-pressed={isAssociated}
                      onClick={() => {
                        setFieldValue('associatedCustomFields', {
                          ...values.associatedCustomFields,
                          [fieldUri]: !isAssociated,
                        });
                      }}
                    >
                      <span className="tooling-stock-custom-field-row__label">{fieldLabel}</span>
                      <span className="tooling-stock-custom-field-row__indicator" aria-hidden="true">
                        {isAssociated ? 'Selected' : 'Tap to update'}
                      </span>
                    </button>
                    {isAssociated ? (
                      <div className="mt-2">
                        {renderCustomFieldInput(customFieldReference)}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <button
              type="submit"
              className="btn btn-lg btn-primary btn-block"
              disabled={isSubmitting}
            >
              Confirm
            </button>
            <button
              type="button"
              className="btn btn-lg btn-link btn-block"
              disabled={isSubmitting}
              onClick={closePanel}
            >
              Cancel
            </button>
          </Form>
        );
      }}
    </Formik>
  );
};

ToolActionForm.propTypes = {
  tool: PropTypes.shape({
    location: PropTypes.string.isRequired,
    // eslint-disable-next-line camelcase
    sub_location: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
  action: PropTypes.string.isRequired,
  closePanel: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  setError: PropTypes.func,
  customFieldReferences: PropTypes.arrayOf(
    PropTypes.shape({
      uri: PropTypes.string.isRequired,
      'field_name': PropTypes.string,
      'field_id': PropTypes.string,
      type: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
        value: PropTypes.string,
      })),
    })
  ),
};

ToolActionForm.defaultProps = {
  setError: null,
  customFieldReferences: [],
};

export default ToolActionForm;
